/**
 * Site navigation tabs shared by `TopBar` (desktop) and `MobileDrawer`.
 *
 * Top-bar IA (2026-10-15, simulated four-persona label + first-click study):
 * plain-language labels, insight first (Overview → Agent readiness → Agents →
 * Activity), then the store tools (Held orders → Rules → Products), then
 * Settings. Benchmarks (synthetic cohort data) and Live Agent (read as a
 * real-time feed, not a simulator) are off the bar; their routes still resolve.
 *
 * Held orders only shows for sites that can have one (founder 2026-10-15):
 * a website with no store has nothing to hold. It fails open — the tab stays
 * while anything is pending, on a transactional site, on a site Sill knows is
 * a store, and whenever the Held orders view itself is open (email deep link).
 */

import { useEffect, useMemo, useState } from 'react';

import type { ReadinessPlatformKind, SiteId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { usePendingEscalationsStore } from '../../app/pending-escalations-context.js';
import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import { derivePlatformKind } from '../../lib/platform-kind.js';

export interface TabDef {
  id: View;
  label: string;
}

export const SITE_TABS: readonly TabDef[] = [
  { id: 'dashboard', label: 'Overview' },
  { id: 'readiness', label: 'Agent readiness' },
  { id: 'agents', label: 'Agents' },
  { id: 'mandates', label: 'Activity' },
  // Founder 2026-10-20: Security slots between Activity and Held orders.
  // Attack senders moved out of Activity → Suspicious here; Blocking +
  // Attack paths moved out of Settings → Blocking here. See
  // `apps/dashboard/src/views/security/Security.tsx`.
  { id: 'security', label: 'Security' },
  { id: 'resolution', label: 'Held orders' },
  { id: 'guardrails', label: 'Rules' },
  { id: 'catalog', label: 'Products' },
  { id: 'settings', label: 'Settings' },
];

const KIND_KEY = (site_id: string): string => `sill:platform_kind:${site_id}`;

function readKind(site_id: string): ReadinessPlatformKind | null {
  try {
    const v = localStorage.getItem(KIND_KEY(site_id));
    return v === 'store' || v === 'website' ? v : null;
  } catch {
    return null;
  }
}

function writeKind(site_id: string, kind: ReadinessPlatformKind): void {
  try {
    localStorage.setItem(KIND_KEY(site_id), kind);
  } catch {
    // Storage blocked — the next load re-derives it.
  }
}

/**
 * Store vs website for the active site, by the same rule Settings and the
 * Agents headline use (`derivePlatformKind`). Seeds from the last answer for
 * this site so the bar does not reflow on every load, then refreshes once per
 * site. `null` while unknown.
 */
function useSitePlatformKind(site_id: SiteId | null): ReadinessPlatformKind | null {
  const api = useApi();
  const [state, setState] = useState<{ site_id: SiteId | null; kind: ReadinessPlatformKind | null }>(() => ({
    site_id,
    kind: site_id === null ? null : readKind(site_id),
  }));
  const kind = state.site_id === site_id ? state.kind : site_id === null ? null : readKind(site_id);

  useEffect(() => {
    if (site_id === null) return;
    let cancelled = false;
    // Wrapped so a synchronous throw (missing client method) lands as a
    // rejection, not a render crash.
    void Promise.allSettled([
      (async () => api.sites.getInstallCoverage(site_id))(),
      (async () => api.visibility.getReport(site_id))(),
    ]).then(([install, visibility]) => {
      if (cancelled) return;
      // Both calls failed: keep what we had rather than guess.
      if (install.status === 'rejected' && visibility.status === 'rejected') return;
      const next = derivePlatformKind({
        install: install.status === 'fulfilled' ? install.value : null,
        visibility: visibility.status === 'fulfilled' ? visibility.value : null,
      });
      writeKind(site_id, next);
      setState({ site_id, kind: next });
    });
    return () => {
      cancelled = true;
    };
  }, [api, site_id]);

  return kind;
}

/** The tabs to render for the active site. */
export function useSiteTabs(view: View): readonly TabDef[] {
  const { activeSite } = useSites();
  const { count: pending } = usePendingEscalationsStore();
  const kind = useSitePlatformKind(activeSite?.site_id ?? null);
  const showHeldOrders =
    view === 'resolution' ||
    pending > 0 ||
    activeSite?.status === 'transactional_active' ||
    kind === 'store';
  return useMemo(
    () => (showHeldOrders ? SITE_TABS : SITE_TABS.filter((t) => t.id !== 'resolution')),
    [showHeldOrders],
  );
}
